/**
 * Tenant warehouse Sales list (`GET /admin/sales`).
 *
 * Default query: last 30 local calendar days, `deleted=false`. Include-voids
 * omits `deleted`. Walk-in is `sourceClientId === null` (not a null
 * `memberName`). Sort omit-default is `saleDate` desc.
 */
import { useMemo, useState } from 'react';
import { Link, Stack } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { lastThirtyLocalDays } from '@/api/warehouse/dateWindow';
import { listSales } from '@/api/warehouse/warehouseApi';
import type { WarehouseSale } from '@/api/warehouse/types';
import { FamilyChip } from '@/components/FamilyChip';
import { RosterTable, type RosterColumn } from '@/components/RosterTable';
import {
  EM_DASH,
  boolLabel,
  formatWarehouseDateTime,
  moneyOrDash,
  textOrDash,
} from '@/components/rosterFormat';
import { useCopy } from '@/hooks/useCopy';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { useResource } from '@/hooks/useResource';

const PAGE_SIZE_DEFAULT = 20;

export function SalesPage() {
  const { t } = useCopy();
  const { i18n } = useTranslation();
  const title = t('page.sales.title', 'Sales');
  useDocumentTitle(title);
  const window = lastThirtyLocalDays();
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(PAGE_SIZE_DEFAULT);
  const [search, setSearch] = useState('');
  const [from, setFrom] = useState(window.from);
  const [to, setTo] = useState(window.to);
  const [includeVoids, setIncludeVoids] = useState(false);
  const [sort, setSort] = useState('saleDate');
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');
  const yes = t('common.yes', 'Yes');
  const no = t('common.no', 'No');
  const walkIn = t('sales.walkIn', 'Walk-in');

  const resource = useResource(
    () => listSales({ page, size, search, from, to, includeVoids, sort, order }),
    [page, size, search, from, to, includeVoids, sort, order],
  );

  const columns = useMemo<RosterColumn<WarehouseSale>[]>(
    () => [
      {
        id: 'saleDate',
        header: t('sales.columns.saleDate', 'Sale date'),
        sortId: 'saleDate',
        defaultOrder: 'desc',
        render: (row) => formatWarehouseDateTime(row.saleDate, i18n.language),
      },
      {
        id: 'documentNumber',
        header: t('sales.columns.documentNumber', 'Document'),
        sortId: 'documentNumber',
        render: (row) => textOrDash(row.documentNumber),
      },
      {
        id: 'member',
        header: t('sales.columns.member', 'Member'),
        sortId: 'memberName',
        render: (row) =>
          row.sourceClientId === null ? (
            walkIn
          ) : (
            <Link component={RouterLink} to={`/users/${row.sourceClientId}`}>
              {textOrDash(row.memberName)}
            </Link>
          ),
      },
      {
        id: 'staff',
        header: t('sales.columns.staff', 'Staff'),
        render: (row) => textOrDash(row.sourceUserId),
      },
      {
        id: 'total',
        header: t('sales.columns.total', 'Total'),
        sortId: 'total',
        render: (row) => moneyOrDash(row.total, row.currency),
      },
      {
        id: 'deleted',
        header: t('sales.columns.deleted', 'Voided'),
        render: (row) => boolLabel(row.deleted, yes, no),
      },
      {
        id: 'products',
        header: t('sales.columns.products', 'Products'),
        sortId: 'productName',
        render: (row) =>
          row.details.length === 0 ? (
            EM_DASH
          ) : (
            <>
              {row.details.map((detail) => (
                <Stack
                  key={String(detail.sourceDetailId)}
                  direction="row"
                  useFlexGap
                  sx={{ flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}
                >
                  {textOrDash(detail.productName)}
                  {detail.families.map((m) => (
                    <FamilyChip
                      key={`${m.dimensionSlug}:${m.familySlug}`}
                      dimensionName={m.dimensionName}
                      familyName={m.familyName}
                      color={m.color}
                      size="small"
                    />
                  ))}
                </Stack>
              ))}
            </>
          ),
      },
    ],
    [i18n.language, no, t, walkIn, yes],
  );

  return (
    <RosterTable
      title={title}
      columns={columns}
      rows={resource.data?.content ?? []}
      rowId={(row) => String(row.sourceSaleId)}
      status={resource.status}
      error={resource.error}
      totalElements={resource.data?.totalElements ?? 0}
      page={page}
      size={size}
      showDates
      showIncludeVoids
      initialFrom={window.from}
      initialTo={window.to}
      sort={sort}
      order={order}
      onPageChange={setPage}
      onRowsPerPageChange={(next) => {
        setSize(next);
        setPage(0);
      }}
      onSortChange={(id, nextOrder) => {
        setSort(id);
        setOrder(nextOrder);
        setPage(0);
      }}
      onApply={(query) => {
        setSearch(query.search);
        setFrom(query.from);
        setTo(query.to);
        setIncludeVoids(query.includeVoids);
        setPage(0);
      }}
      onRetry={resource.refetch}
    />
  );
}
